<script lang="ts" setup>
import InputSuffix from '@/components/InputSuffix.vue'
import { ref } from 'vue'

const props = defineProps<{
  id: string
  originalPrice: number
}>()

const emit = defineEmits<{
  (e: 'confirm', value: string | number | undefined): void
}>()

const percentStr = ref<string>('0')
const isOpen = ref<boolean>(false)
const debounce = ref<boolean>(false)
const button = ref<HTMLButtonElement | null>(null)
const popup = ref<HTMLDivElement | null>(null)
const root = ref<HTMLDivElement | null>(null)

function getPercent(): number {
  return parseInt(percentStr.value) || 0
}

function confirmClicked() {
  const newPrice = (1 - getPercent() / 100) * (props.originalPrice || 0)
  emit('confirm', getPercent() ? newPrice : undefined)
  isOpen.value = false
  percentStr.value = '0'
}

function changeClicked(e: Event, increase: boolean) {
  const step = increase ? 1 : -1
  if (getPercent() + step > 100 || getPercent() + step < 0) {
    return
  }
  (e.currentTarget as HTMLButtonElement).focus()
  percentStr.value = (getPercent() + step).toString()
}

function calculatorBtnClicked() {
  isOpen.value = !isOpen.value
  button.value?.focus()
}

function inputChanged() {
  percentStr.value = percentStr.value.replace(/[^-\d]/g, '')
  if (getPercent() > 100) {
    percentStr.value = '100'
  }
}

/**
 * Checks with the help of relatedTarget if an element of the list got focused and exits the function
 * Otherwise it means a click occurred outside the select element and the dropdown gets closed
 */
function blurred(e: FocusEvent): void {
  if (root.value?.contains(e.relatedTarget as HTMLElement)) {
    return
  }

  if (e.target == e.relatedTarget) {
    return
  }

  isOpen.value = false
  debounce.value = true
  setTimeout(() => {
    debounce.value = false
  }, 200)
}
</script>

<template>
  <div ref="root" :class="{ 'is--open': isOpen }" class="calculator">
    <div ref="popup" class="calculator__pop-up" tabindex="999" @focusout="blurred">
      <div class="calculator__label span">
        {{ $t('step_summary.enter_discount') }}
      </div>
      <div class="calculator__controls">
        <button
          :class="{ 'is--disabled': getPercent() <= 0 }"
          class="btn is--tertiary calculator__change-btn is--decrease"
          @click="changeClicked($event, false)"
        >
          <i class="ic-minus" />
        </button>
        <InputSuffix
          :id="'calculator-input-' + id"
          :key="'calculator-input-' + id"
          v-model="percentStr"
          :show-suffix-on-empty="true"
          placeholder=""
          suffix=" %"
          @update:model-value="inputChanged"
        />
        <button
          :class="{ 'is--disabled': getPercent() >= 100 }"
          class="btn is--tertiary calculator__change-btn is--increase"
          @click="changeClicked($event, true)"
        >
          <i class="ic-plus" />
        </button>
      </div>
      <button class="btn calculator__confirm is--tertiary" @click="confirmClicked">
        {{ $t('step_summary.btn_confirm') }}
      </button>
    </div>
    <button ref="button" class="calculator__button btn is--tertiary" @click="calculatorBtnClicked" @focusout="blurred">
      <i v-if="isOpen" class="ic-times" />
      <i v-else class="ic-calc" />
    </button>
  </div>
</template>

<style lang="scss">
@import '@/assets/style/helpers/include-media';

$gap: 8;
$controlsHeight: 32;
$arrowHeight: 15;
$arrowWidth: 17;

.calculator {
  position: relative;
  z-index: 1;

  &__pop-up {
    position: absolute;
    bottom: calc(100% + #{rem-calc($arrowHeight + 4)});
    right: rem-calc(-12);
    width: rem-calc(164);
    padding: rem-calc(12);

    //Transition
    transition: opacity $animTimeShort ease, transform $animTimeShort ease, visibility 0s ease $animTimeShort;
    opacity: 0;
    visibility: hidden;
    transform: scale3d(0.7, 0.7, 1);
    transform-origin: rem-calc(164 - 23 - $arrowWidth * 0.5) bottom;

    @include themify($themes) {
      border-radius: rem-calc(themed('controlBtnBorderRadius'));
      background: themed('primaryColorDark');
    }

    .package__badge & {
      background: $colorButtonGrey;
    }

    .is--open & {
      opacity: 1;
      transform: scale3d(1, 1, 1);
      visibility: visible;
      transition: opacity $animTimeShort ease, transform $animTimeShort ease, visibility 0s ease 0s;
    }

    &:after {
      content: '';
      position: absolute;
      bottom: rem-calc(-$arrowHeight);
      right: rem-calc(23);
      border-top: rem-calc($arrowHeight) solid #000;
      border-left: rem-calc($arrowWidth * 0.5) solid transparent;
      border-right: rem-calc($arrowWidth * 0.5) solid transparent;

      @include themify($themes) {
        border-top-color: themed('primaryColorDark');
      }
    }
  }

  &__controls {
    display: flex;
    margin-left: rem-calc(-$gap * 0.5);
    margin-right: rem-calc(-$gap * 0.5);

    & > *,
    & > .btn {
      margin-left: rem-calc($gap * 0.5);
      margin-right: rem-calc($gap * 0.5);
    }

    input[type='text'] {
      width: 100%;
      height: rem-calc($controlsHeight);
      min-height: rem-calc($controlsHeight);
    }

    .inputsuffix__wrapper {
      flex-grow: 1;
      flex-shrink: 1;
      color: $textColor;
      background: #fff;

      @include themify($themes) {
        border-radius: rem-calc(themed('controlBtnBorderRadius'));
      }
    }
  }

  &__change-btn {
    &.btn {
      width: rem-calc($controlsHeight);
      height: rem-calc($controlsHeight);
      padding: 0;
      min-width: auto;
      flex-grow: 0;
      flex-shrink: 0;
      display: flex;
      justify-content: center;
      align-items: center;

      i[class^='ic'],
      i[class*=' ic'] {
        font-size: rem-calc(32);
        margin-right: 0;
      }

      &.is--disabled {
        @include themify($themes) {
          background: $controlButtonDisabledColor;
          border-color: $controlButtonDisabledColor;
          opacity: 1;
        }

        &:hover {
          @include themify($themes) {
            background: $controlButtonDisabledColor;
            border-color: $controlButtonDisabledColor;
            opacity: 1;
          }
        }
      }

      @include themify($themes) {
        border-radius: rem-calc(themed('controlBtnBorderRadius'));
      }
    }
  }

  &__confirm {
    &.btn {
      display: flex;
      width: 100%;
      height: rem-calc($controlsHeight);
      flex-grow: 0;
      flex-shrink: 0;
      margin-top: rem-calc($gap);
      min-width: auto;
      line-height: 1;

      @include themify($themes) {
        border-radius: rem-calc(themed('controlBtnBorderRadius'));
      }
    }
  }

  &__button {
    &.btn {
      width: rem-calc($inputHeight);
      height: rem-calc($inputHeight);
      min-width: auto;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;

      i[class^='ic'],
      i[class*=' ic'] {
        margin: 0;
      }

      .ic-times {
        font-size: rem-calc(28);
      }

      @include themify($themes) {
        border-radius: rem-calc(themed('controlBtnBorderRadius'));
      }
    }

    .is--open & {
      &.btn {
        @include themify($themes) {
          background: themed('primaryColorDark');
          border-color: themed('primaryColorDark');
        }

        &:hover {
          @include themify($themes) {
            background: themed('primaryColor');
            border-color: themed('primaryColor');
          }
        }
      }
    }

    .package__badge & {
      &.btn.is--tertiary {
        @include themify($themes) {
          background-color: $colorButtonGrey;
          border: $colorButtonGrey;

          &:hover {
            background-color: $colorButtonGreyHover;
            border: $colorButtonGreyHover;
          }
        }
      }
    }
  }

  &__label {
    margin-bottom: rem-calc($gap);
    text-align: center;
    color: #fff;
  }
}
</style>
